import { useId, type ReactNode } from 'react';
import { cx } from './cx';

interface C1PanelProps {
  title: ReactNode;
  /** Trailing control on the title row, e.g. a "View all" link. */
  action?: ReactNode;
  children: ReactNode;
  /** Pinned under the body, separated by a rule — a total, or a secondary action. */
  footer?: ReactNode;
  /** Drops the body padding, for a list or table that should meet the card edge. */
  flush?: boolean;
  className?: string;
}

/**
 * A titled card: the box a dashboard region sits in.
 *
 * THREE CONTAINERS, THREE JOBS, AND THEY ARE NOT INTERCHANGEABLE. `C1Section` is a
 * heading over page content with no surface of its own. `C1ChartCard` is a
 * `<figure>`, for a graphic whose title belongs to the graphic. This is a labelled
 * `region` for a block of mixed content — a list, a table, a set of figures — that
 * a reader should be able to jump to by name.
 */
export function C1Panel({ title, action, children, footer, flush, className }: C1PanelProps) {
  const headingId = useId();

  return (
    <section className={cx('c1-panel', className)} aria-labelledby={headingId}>
      <div className="c1-panelHeader">
        <h3 className="c1-panelTitle" id={headingId}>
          {title}
        </h3>
        {action}
      </div>
      <div className={cx('c1-panelBody', flush && 'c1-panelBody--flush')}>{children}</div>
      {footer ? <div className="c1-panelFooter">{footer}</div> : null}
    </section>
  );
}
